/* ═══════════════════════════════════════════════════════
   Worker Icon System — Centralized outcome-based icons
   ═══════════════════════════════════════════════════════ */

const CDN = 'https://customer-assets.emergentagent.com/job_03696536-1730-433f-a0b0-c8e6e71d4e66/artifacts';
const ORION_CDN = 'https://customer-assets.emergentagent.com/job_orion-ai-4/artifacts';

export const WORKER_ICONS = {
  communication: `${CDN}/zphm9pod_01-Customer%20enquiry%20reply-icon.png`,
  sales:         `${CDN}/x8eg9hhb_05-Sales%20funnel%20Automation-icon.png`,
  analysis:      `${CDN}/jlswz3f7_08-document%20analysis-icon.png`,
  admin:         `${CDN}/9liqmhxi_12-Task%20Creator%20-Icon.png`,
  marketing:     `${CDN}/6wod76dd_13-Content%20Gennerator-icon.png`,
  scheduling:    `${CDN}/wt6rlfsl_15-Appointment%20Booking-icon.png`,
  research:      `${CDN}/8c6kogro_09-Report%20generator-icon.png`,
  system:        `${ORION_CDN}/pxvnoi7t_Orion%201.png`,
};

// Category → icon type mapping
const CATEGORY_MAP = {
  'Lead & Enquiries':      'sales',
  'Bookings & Scheduling': 'scheduling',
  'Quotes & Revenue':      'sales',
  'Payments & Admin':      'admin',
  'Reviews & Reputation':  'communication',
  'Customer Retention':    'marketing',
  'Operations':            'communication',
  'Advanced':              'research',
  // Legacy fallbacks
  'Customer Support':      'communication',
  'Increase Sales':        'sales',
  'Finance & Admin':       'admin',
  'Marketing':             'marketing',
  'Research & Analysis':   'analysis',
  'Reduce Admin':          'admin',
  'Save Time':             'scheduling',
};

// Template-level overrides (name → icon type)
const TEMPLATE_OVERRIDES = {
  // Lead & Enquiries
  'Never Miss a Lead': 'sales',
  'Missed Call Recovery': 'communication',
  'Lead Qualification': 'analysis',
  // Bookings & Scheduling
  'Booking Assistant': 'scheduling',
  'Appointment Reminders': 'scheduling',
  // Quotes & Revenue
  'Send Quotes Fast': 'admin',
  'Quote Follow-Up': 'sales',
  'Upsell & Cross-Sell': 'sales',
  // Payments & Admin
  'Send Invoices': 'admin',
  'Chase Unpaid Invoices': 'admin',
  // Reviews & Reputation
  'Get More Reviews': 'communication',
  'Handle Negative Feedback': 'communication',
  // Customer Retention
  'Rebook Past Customers': 'marketing',
  'Seasonal Promotions': 'marketing',
  // Operations
  'Customer Enquiry Handler': 'communication',
  'Customer Enquiry Worker': 'communication',
  'After-Hours Response': 'communication',
  'After-hours enquiry response': 'communication',
  // Advanced
  'Job Follow-Up Assistant': 'communication',
  'Internal Admin Assistant': 'research',
  'File / Analysis Worker': 'analysis',
  // Specialty
  'Voice S2S Worker': 'communication',
  'Orion Operator': 'system',
};

/**
 * Get the icon URL for a worker based on its name and category.
 * Checks template overrides first, then fuzzy name match, then category mapping.
 * @param {string} category - The outcome_category of the worker
 * @param {string} name - The name of the worker
 * @returns {string} URL to the icon image
 */
export function getWorkerIcon(category, name) {
  // Check template-level overrides first (exact match)
  if (name && TEMPLATE_OVERRIDES[name]) {
    return WORKER_ICONS[TEMPLATE_OVERRIDES[name]];
  }

  // Fuzzy name matching for dynamically created workers
  if (name) {
    const lower = name.toLowerCase();
    if (lower.includes('enquir') || lower.includes('email') || lower.includes('customer') || lower.includes('response') || lower.includes('reply'))
      return WORKER_ICONS.communication;
    if (lower.includes('lead') || lower.includes('sales') || lower.includes('funnel') || lower.includes('pipeline'))
      return WORKER_ICONS.sales;
    if (lower.includes('file') || lower.includes('analy') || lower.includes('document') || lower.includes('report'))
      return WORKER_ICONS.analysis;
    if (lower.includes('appointment') || lower.includes('book') || lower.includes('schedul') || lower.includes('calendar') || lower.includes('remind'))
      return WORKER_ICONS.scheduling;
    if (lower.includes('invoice') || lower.includes('payment') || lower.includes('admin') || lower.includes('task'))
      return WORKER_ICONS.admin;
    if (lower.includes('review') || lower.includes('feedback') || lower.includes('marketing') || lower.includes('promo'))
      return WORKER_ICONS.marketing;
    if (lower.includes('content') || lower.includes('social') || lower.includes('campaign'))
      return WORKER_ICONS.marketing;
    if (lower.includes('research') || lower.includes('data') || lower.includes('insight'))
      return WORKER_ICONS.research;
  }

  // Fall back to category mapping
  if (category && CATEGORY_MAP[category]) {
    return WORKER_ICONS[CATEGORY_MAP[category]];
  }

  // Default fallback
  return WORKER_ICONS.admin;
}

/**
 * Get the icon type string for a worker (e.g. 'sales', 'admin')
 */
export function getWorkerIconType(category, name) {
  if (name && TEMPLATE_OVERRIDES[name]) {
    return TEMPLATE_OVERRIDES[name];
  }
  if (category && CATEGORY_MAP[category]) {
    return CATEGORY_MAP[category];
  }
  return 'admin';
}
